import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'

import { attachInterceptors } from './interceptors'

/**
 * Global Axios instance with interceptors attached.
 * Timeout set to 20 seconds.
 */
export const axiosInstance: AxiosInstance = axios.create({
  timeout: 20000,
})

// Attach request/response interceptors
attachInterceptors(axiosInstance)

/**
 * HTTP config type for additional request configuration
 */
export type HttpConfig = AxiosRequestConfig

/**
 * Unwraps AxiosResponse to return just the data.
 * Useful for service layer methods.
 *
 * @param response - The Axios response to unwrap
 * @returns The response data
 */
export function unwrap<T>(response: AxiosResponse<T>): T {
  return response.data
}

/**
 * HTTP client wrapper with common methods.
 * All methods return AxiosResponse for maximum flexibility.
 */
export const http = {
  get: <T>(url: string, config?: HttpConfig): Promise<AxiosResponse<T>> => axiosInstance.get<T>(url, config),
  post: <T>(url: string, data?: unknown, config?: HttpConfig) => axiosInstance.post<T>(url, data, config),
  put: <T>(url: string, data?: unknown, config?: HttpConfig): Promise<AxiosResponse<T>> =>
    axiosInstance.put<T>(url, data, config),
  patch: <T>(url: string, data?: unknown, config?: HttpConfig): Promise<AxiosResponse<T>> =>
    axiosInstance.patch<T>(url, data, config),
  delete: <T>(url: string, config?: HttpConfig): Promise<AxiosResponse<T>> => axiosInstance.delete<T>(url, config),
}

/**
 * Helper to create config that skips automatic auth token injection.
 * Useful for public endpoints like /api/config.
 *
 * @example
 * ```ts
 * http.get('/api/config', withoutAuth())
 * ```
 */
export function withoutAuth(): HttpConfig {
  return {
    headers: {
      Authorization: null,
    },
  }
}
